import { Reducer, Action } from 'redux';
import { call, select, all, delay, put, takeEvery, fork } from 'redux-saga/effects';
import { actionCreators as layoutActionCreators } from 'src/reducers/Layout';
import { selectors as authSelectors } from 'src/reducers/Auth';
import Api from '../api';
import OwnershipContactCategoryForm from 'src/models/OwnershipContactCategoryForm';
import Ownership from 'src/models/tract_detail/Ownership';

export interface IOwnershipContactCategoriesState {
    categories: OwnershipContactCategoryForm;
    committedCategoriesIds: number[];
    attachedOwners: Array<Ownership>;
}

const initialState: IOwnershipContactCategoriesState = {
    categories: undefined,
    committedCategoriesIds: [2, 3, 4, 5],
    attachedOwners: undefined
};

export enum actionTypes {
    GET_OWNERSHIP_CONTACT_CATEGORIES = '[ownershipContactCategories] GET_OWNERSHIP_CONTACT_CATEGORIES',
    GET_OWNERSHIP_CONTACT_CATEGORIES_SUCCESS = '[ownershipContactCategories] GET_OWNERSHIP_CONTACT_CATEGORIES_SUCCESS',
    GET_OWNERSHIP_CONTACT_CATEGORIES_FORM = '[ownershipContactCategories] GET_OWNERSHIP_CONTACT_CATEGORIES_FORM',
    GET_OWNERSHIP_CONTACT_CATEGORIES_FORM_SUCCESS = '[ownershipContactCategories] GET_OWNERSHIP_CONTACT_CATEGORIES_FORM_SUCCESS',
    GET_ATTACHED_OWNERS = '[ownershipContactCategories] GET_ATTACHED_OWNERS',
    GET_ATTACHED_OWNERS_SUCCESS = '[ownershipContactCategories] GET_ATTACHED_OWNERS_SUCCESS',
    DUMP_CATEGORIES = '[ownershipContactCategories] DUMP_CATEGORIES',
    SAVE_OWNERSHIP_CONTACT_CATEGORIES = '[ownershipContactCategories] SAVE_OWNERSHIP_CONTACT_CATEGORIES',
}

export const actionCreators = {
    getOwnershipContactCategoriesForm: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES_FORM, payload: { clientNumber, projectNumber } };
    },

    getOwnershipContactCategoriesFormSuccess: (ownershipContactCategories: OwnershipContactCategoryForm) => {
        return { type: actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES_FORM_SUCCESS, payload: ownershipContactCategories };
    },
    getOwnershipContactCategories: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES, payload: { clientNumber, projectNumber } };
    },
    getOwnershipContactCategoriesSuccess: (ownershipContactCategories: OwnershipContactCategoryForm) => {
        return { type: actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES_SUCCESS, payload: ownershipContactCategories };
    },
    getAttachedOwners: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_ATTACHED_OWNERS, payload: { clientNumber, projectNumber } };
    },
    getAttachedOwnersSuccess: (ownerships: Array<Ownership>) => {
        return { type: actionTypes.GET_ATTACHED_OWNERS_SUCCESS, payload: ownerships };
    },
    saveOwnershipContactCategories: (ownershipContactCategoryForm: OwnershipContactCategoryForm, clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.SAVE_OWNERSHIP_CONTACT_CATEGORIES, payload: { ownershipContactCategoryForm, clientNumber, projectNumber } };
    },
    dumpCategories: () => {
        return { type: actionTypes.DUMP_CATEGORIES };
    },
};

const reducer: Reducer<IOwnershipContactCategoriesState> = (
    state: IOwnershipContactCategoriesState = initialState,
    action: Action
): IOwnershipContactCategoriesState => {
    switch (action.type) {
        case actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES_FORM_SUCCESS: {
            const payload = (action as { type: string; payload: OwnershipContactCategoryForm }).payload;
            for (let c of payload.categories) {
                if (state.committedCategoriesIds.includes(c.id)) {
                    c.isCrossProjectChecked = true;
                }
            }
            return {
                ...state,
                categories: payload,
            };
        }
        case actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES_SUCCESS: {
            const payload = (action as { type: string; payload: OwnershipContactCategoryForm }).payload;
            for (let c of payload.categories) {
                if (state.committedCategoriesIds.includes(c.id)) {
                    c.isCrossProjectChecked = true;
                }
            }
            return {
                ...state,
                categories: payload,
            };
        }
        case actionTypes.GET_ATTACHED_OWNERS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Ownership> }).payload;
            return {
                ...state,
                attachedOwners: payload
            };
        }
        case actionTypes.DUMP_CATEGORIES: {
            return {
                ...state,
                categories: undefined,
            };
        }
        default:
            return state;
    }
};

export default reducer;

const sagas = {
    getOwnershipContactCategoriesForm: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            call(Api.OwnershipContactCategories.getForm, action.payload.clientNumber, action.payload.projectNumber, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getOwnershipContactCategoriesFormSuccess(result));
    },
    getOwnershipContactCategories: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        const [result] = yield all([
            call(Api.OwnershipContactCategories.get, action.payload.clientNumber, action.payload.projectNumber, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getOwnershipContactCategoriesSuccess(result));
    },
    getAttachedOwners: function* (action: { type: string; payload: { clientNumber: number, projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(Api.OwnershipContactCategories.getAttachedOwners, action.payload.clientNumber, action.payload.projectNumber, jwt),
            delay(200)
        ]);
        yield put(actionCreators.getAttachedOwnersSuccess(result));
    },
    saveOwnershipContactCategories: function* (action: {
        type: string;
        payload: { ownershipContactCategoryForm: OwnershipContactCategoryForm; clientNumber: number; projectNumber: number };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);

        yield all([
            call(
                Api.OwnershipContactCategories.save,
                action.payload.ownershipContactCategoryForm,
                action.payload.clientNumber,
                action.payload.projectNumber,
                jwt
            ),
            delay(500),
        ]);
        yield put(actionCreators.dumpCategories());
        yield put(actionCreators.getOwnershipContactCategoriesForm(action.payload.clientNumber, action.payload.projectNumber));
    },
};

const watches = {
    getOwnershipContactCategoriesForm: function* () {
        yield takeEvery(actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES_FORM, sagas.getOwnershipContactCategoriesForm);
    },
    getOwnershipContactCategories: function* () {
        yield takeEvery(actionTypes.GET_OWNERSHIP_CONTACT_CATEGORIES, sagas.getOwnershipContactCategories);
    },
    getAttachedOwnerships: function* () {
        yield takeEvery(actionTypes.GET_ATTACHED_OWNERS, sagas.getAttachedOwners);
    },
    saveOwnershipContactCategories: function* () {
        yield takeEvery(actionTypes.SAVE_OWNERSHIP_CONTACT_CATEGORIES, sagas.saveOwnershipContactCategories);
    },
};
export function* saga() {
    yield fork(watches.getOwnershipContactCategoriesForm);
    yield fork(watches.getOwnershipContactCategories);
    yield fork(watches.getAttachedOwnerships);
    yield fork(watches.saveOwnershipContactCategories);
}



// WEBPACK FOOTER //
// ./src/reducers/OwnershipContactCategories.ts